import {
    BLEND_NORMAL,
    Color,
    CULLFACE_NONE,
    Entity,
    Ray,
    StandardMaterial,
    math,
    Vec3
} from 'playcanvas';

import { Element, ElementType } from './element';

type PictureLike = {
    id: string,
    position?: Vec3 | { x: number, y: number, z: number },
    target?: Vec3 | { x: number, y: number, z: number },
    validated?: boolean
};

type MarkerState = {
    id: string,
    entity: Entity,
    position: Vec3,
    validated: boolean
};

const VALIDATED_COLOR = new Color(0.22, 0.88, 0.40);
const UNVALIDATED_COLOR = new Color(0.98, 0.80, 0.22);
const SELECTED_TINT = new Color(1.0, 1.0, 1.0);

const tmpCamPos = new Vec3();
const tmpRay = new Ray();
const tmpSub = new Vec3();

const rayIntersectsSphere = (ray: Ray, center: Vec3, radius: number): number | null => {
    const toCenter = tmpSub.sub2(ray.origin, center);
    const b = toCenter.dot(ray.direction);
    const c = toCenter.dot(toCenter) - radius * radius;

    if (c > 0 && b > 0) {
        return null;
    }

    const discriminant = b * b - c;
    if (discriminant < 0) {
        return null;
    }

    let t = -b - Math.sqrt(discriminant);
    if (t < 0) {
        t = 0;
    }

    return t;
};

class PhotoHotspots extends Element {
    root: Entity;
    markers = new Map<string, MarkerState>();
    private readonly onPointerDown: (e: PointerEvent) => void;
    private lastActivatedMarkerId: string | null = null;
    private lastActivatedAtMs = 0;

    markerPixelSize = 18;
    minScale = 0.05;
    maxScale = 3.0;
    selectedScaleMultiplier = 1.35;
    activationDebounceMs = 250;

    dirtyMarkers = true;
    dirtySelection = true;
    enabled = false;

    constructor() {
        super(ElementType.debug);

        this.onPointerDown = (e: PointerEvent) => {
            if (!this.enabled || e.button !== 0) {
                return;
            }

            const hitId = this.pickMarker(e);
            if (!hitId) {
                return;
            }

            e.stopPropagation();

            const pictures = (this.scene.events.invoke('pictures.list') as PictureLike[]) ?? [];
            const hitPicture = pictures.find(picture => String(picture.id) === hitId);

            const now = performance.now();
            if (this.lastActivatedMarkerId === hitId && (now - this.lastActivatedAtMs) < this.activationDebounceMs) {
                return;
            }
            this.lastActivatedMarkerId = hitId;
            this.lastActivatedAtMs = now;

            this.scene.events.fire('pictures.select', hitId);

            if (hitPicture?.position && hitPicture?.target) {
                this.scene.events.fire('camera.setPose', {
                    position: hitPicture.position,
                    target: hitPicture.target
                }, 0);
            }
        };
    }

    add() {
        const { scene } = this;

        this.root = new Entity('photoHotspots');
        this.root.enabled = false;
        scene.contentRoot.addChild(this.root);

        const markMarkersDirty = () => {
            this.dirtyMarkers = true;
            scene.forceRender = true;
        };

        const markSelectionDirty = () => {
            this.dirtySelection = true;
            scene.forceRender = true;
        };

        const updateEnabled = () => {
            const next = !!scene.events.invoke('picture.enabled') || !!scene.events.invoke('pictures.cues.enabled');
            if (next !== this.enabled) {
                this.enabled = next;
                this.root.enabled = next;
                scene.forceRender = true;
            }
        };

        const { events } = scene;
        events.on('pictures.listChanged', markMarkersDirty);
        events.on('camera.posesChanged', markMarkersDirty);
        events.on('scene.clear', markMarkersDirty);

        events.on('pictures.selectedChanged', markSelectionDirty);

        events.on('picture.enabledChanged', updateEnabled);
        events.on('camera.posesValidationChanged', updateEnabled);
        events.on('pictures.cues.enabledChanged', updateEnabled);

        const canvasContainer = window.document.getElementById('canvas-container');
        canvasContainer?.addEventListener('pointerdown', this.onPointerDown, true);

        updateEnabled();
    }

    remove() {
        const canvasContainer = window.document.getElementById('canvas-container');
        canvasContainer?.removeEventListener('pointerdown', this.onPointerDown, true);

        this.root?.destroy();
        this.markers.clear();
    }

    onPreRender() {
        if (!this.root) {
            return;
        }

        this.root.enabled = this.enabled;
        if (!this.enabled) {
            return;
        }

        if (this.dirtyMarkers) {
            this.dirtyMarkers = false;
            this.rebuildMarkers();
            this.dirtySelection = true;
        }

        if (this.dirtySelection) {
            this.dirtySelection = false;
            this.refreshSelectionStyling();
        }

        this.updateBillboardScale();
    }

    private clearMarkers() {
        for (const marker of this.markers.values()) {
            marker.entity.destroy();
        }
        this.markers.clear();
    }

    private createMarkerMaterial(validated: boolean, selected: boolean) {
        const mat = new StandardMaterial();
        mat.useLighting = false;
        mat.cull = CULLFACE_NONE;
        mat.opacity = selected ? 1.0 : 0.95;
        mat.blendType = BLEND_NORMAL;
        mat.depthTest = true;
        mat.depthWrite = true;

        const base = validated ? VALIDATED_COLOR : UNVALIDATED_COLOR;
        const color = selected ? SELECTED_TINT : base;
        mat.emissive = color.clone();
        mat.emissiveIntensity = selected ? 1.9 : 1.35;
        mat.diffuse = color.clone();
        mat.update();
        return mat;
    }

    private rebuildMarkers() {
        this.clearMarkers();

        const pictures = (this.scene.events.invoke('pictures.list') as PictureLike[]) ?? [];
        const selectedId = String(this.scene.events.invoke('pictures.selectedId') ?? '');

        for (const picture of pictures) {
            if (!picture?.position) {
                continue;
            }

            const id = String(picture.id);
            const selected = id === selectedId;
            const validated = picture.validated !== false;
            const position = new Vec3(picture.position.x, picture.position.y, picture.position.z);

            const entity = new Entity(`photoHotspot_${id}`);
            entity.addComponent('render', {
                type: 'plane',
                layers: [this.scene.gizmoLayer.id]
            });

            entity.setPosition(position);
            entity.render.meshInstances.forEach((meshInstance) => {
                meshInstance.material = this.createMarkerMaterial(validated, selected);
                meshInstance.cull = false;
            });

            this.root.addChild(entity);
            this.markers.set(id, {
                id,
                entity,
                position,
                validated
            });
        }
    }

    private refreshSelectionStyling() {
        const selectedId = String(this.scene.events.invoke('pictures.selectedId') ?? '');

        for (const marker of this.markers.values()) {
            const selected = marker.id === selectedId;
            marker.entity.render.meshInstances.forEach((meshInstance) => {
                meshInstance.material = this.createMarkerMaterial(marker.validated, selected);
            });
        }
    }

    private updateBillboardScale() {
        const cameraEntity = this.scene.camera.entity;
        const camera = this.scene.camera.camera;
        const viewportHeight = this.scene.graphicsDevice.height;
        const cameraPos = tmpCamPos.copy(cameraEntity.getPosition());
        const isOrtho = this.scene.camera.ortho;
        const orthoHeight = camera.orthoHeight;
        const fov = camera.fov;

        for (const marker of this.markers.values()) {
            marker.entity.lookAt(cameraPos);

            const distance = marker.position.distance(cameraPos);
            const worldPerPixel = isOrtho ? ((orthoHeight * 2) / Math.max(1, viewportHeight)) :
                ((2 * Math.tan(math.DEG_TO_RAD * fov * 0.5) * Math.max(distance, 0.001)) / Math.max(1, viewportHeight));

            const baseScale = math.clamp(this.markerPixelSize * worldPerPixel, this.minScale, this.maxScale);
            const selectedId = String(this.scene.events.invoke('pictures.selectedId') ?? '');
            const selected = marker.id === selectedId;
            const scale = selected ? baseScale * this.selectedScaleMultiplier : baseScale;

            marker.entity.setLocalScale(scale, scale, scale);
        }
    }

    private pickMarker(e: PointerEvent): string | null {
        const canvasRect = this.scene.canvas.getBoundingClientRect();
        const screenX = e.clientX - canvasRect.left;
        const screenY = e.clientY - canvasRect.top;

        this.scene.camera.getRay(screenX, screenY, tmpRay);

        let bestId: string | null = null;
        let bestDistance = Infinity;

        for (const marker of this.markers.values()) {
            const scale = marker.entity.getLocalScale().x;
            const pickRadius = Math.max(this.minScale * 0.6, scale * 0.7);
            const hitDistance = rayIntersectsSphere(tmpRay, marker.position, pickRadius);

            if (hitDistance !== null && hitDistance < bestDistance) {
                bestDistance = hitDistance;
                bestId = marker.id;
            }
        }

        return bestId;
    }
}

export { PhotoHotspots };
